import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ClientStatsWindowKey,
  CoreWindowStats,
  ClientViewDealerDetail,
  ClientViewVinRow,
  InspectionStats,
  ListingChannelStats,
  ListingStats,
} from '../../api/types';
import { siftApiClient } from '../../api/siftApiClient';
import { TIER_COLOR, TIER_LABEL, classifyTier, vinActionDisplay } from './clientViewTokens';
import {
  USER_STATUS_OPTIONS, USER_STATUS_DISPLAY_DEFAULT, CLIENT_ELECT_NO_LIST, VIN_STATUS_REASON_MAX,
} from '../../types/sift';
import type { UserVinStatus } from '../../types/sift';
import { RdsMiaSummary } from './RdsMiaSummary';
import { LightBadge } from '../shared/LightBadge';
import { lightLabel } from '../shared/lightLabels';
import type { AgentHandoff } from '../agent/AgentChatWidget';
import './DealerInsightsPopout.css';
import { pushUsageContext, track } from '../../telemetry/usage';

interface DealerInsightsPopoutProps {
  sellerId: string;
  onClose: () => void;
  onVinClick?: (vin: ClientViewVinRow) => void;
  /** Mia's "Where to focus" for the dealer (RDS-3.5); hidden when Mia is off for the user. */
  miaEnabled?: boolean;
  onContinueInMia?: (handoff: AgentHandoff) => void;
  /** Tablets and phones: the panel covers the screen and leads with a Back button. */
  fullScreen?: boolean;
}

type SortDir = 'asc' | 'desc';
type SortKey = 'vin' | 'ymm' | 'list_status' | 'inspect_status' | 'inspection_source' | 'site_location' | 'light_status' | 'cr_grade' | 'days_since_inspection' | 'list_days' | 'listing_occurences' | 'days_expired' | 'vin_action_filter' | 'user_status';
interface SortEntry { key: SortKey; dir: SortDir }

interface VinColumn {
  key: SortKey;
  label: string;
  // Value used for sorting (numeric for numeric cols, lowercase string otherwise).
  sortValue: (v: ClientViewVinRow) => string | number | null | undefined;
  // Value used for searching across all columns (string form).
  searchValue: (v: ClientViewVinRow) => string;
  // Rendered cell text.
  render: (v: ClientViewVinRow) => React.ReactNode;
  cellClass?: string;
  numeric?: boolean;
}

const LIGHT_SORT: Record<string, number> = { RED: 0, YELLOW: 1, GREEN: 2, NOT_AS_IS: 3 };

/** Year / make / model / trim; the feed's 'Unknown' trim placeholder is left out. */
const ymmt = (v: ClientViewVinRow): string => {
  const trim = v.trim && v.trim.trim().toLowerCase() !== 'unknown' ? v.trim : null;
  return [v.model_year, v.make, v.model, trim].filter(Boolean).join(' ');
};

const VIN_COLUMNS: VinColumn[] = [
  {
    key: 'user_status',
    label: 'Status',
    sortValue: (v) => (v.user_status || USER_STATUS_DISPLAY_DEFAULT).toLowerCase(),
    searchValue: (v) => v.user_status || USER_STATUS_DISPLAY_DEFAULT,
    // Real cell is rendered inline in the table body (needs closure access for onChange).
    render: (v) => v.user_status || USER_STATUS_DISPLAY_DEFAULT,
    cellClass: 'cv-vin-status-cell',
  },
  {
    key: 'vin',
    label: 'VIN',
    sortValue: (v) => (v.vin || '').toLowerCase(),
    searchValue: (v) => v.vin || '',
    render: (v) => v.vin,
    cellClass: 'cv-vin-cell',
  },
  {
    key: 'ymm',
    label: 'Year / Make / Model / Trim',
    sortValue: (v) => ymmt(v).toLowerCase(),
    searchValue: (v) => ymmt(v),
    render: (v) => ymmt(v) || '–',
  },
  {
    key: 'list_status',
    label: 'List Status',
    sortValue: (v) => (v.list_status || '').toLowerCase(),
    searchValue: (v) => v.list_status || '',
    render: (v) => v.list_status || '–',
  },
  {
    key: 'inspect_status',
    label: 'Inspect',
    sortValue: (v) => (v.inspect_status || '').toLowerCase(),
    searchValue: (v) => v.inspect_status || '',
    render: (v) => v.inspect_status || '–',
  },
  {
    key: 'inspection_source',
    label: 'Insp Source',
    sortValue: (v) => (v.inspection_source || '').toLowerCase(),
    searchValue: (v) => v.inspection_source || '',
    render: (v) => v.inspection_source || '–',
  },
  {
    key: 'site_location',
    label: 'Site',
    sortValue: (v) => (v.site_location || '').toLowerCase(),
    searchValue: (v) => v.site_location || '',
    render: (v) => v.site_location || '–',
  },
  {
    key: 'light_status',
    label: 'Light',
    // Red first when sorted ascending: as-is units are the ones that need a different plan.
    sortValue: (v) => (v.light_status ? LIGHT_SORT[v.light_status.toUpperCase()] ?? 9 : null),
    searchValue: (v) => `${lightLabel(v.light_status)}${v.title_absent_flg ? ' title absent' : ''}`,
    render: (v) => <LightBadge light={v.light_status} titleAbsent={v.title_absent_flg} />,
  },
  {
    key: 'cr_grade',
    label: 'CR',
    sortValue: (v) => v.cr_grade,
    searchValue: (v) => (v.cr_grade != null ? v.cr_grade.toFixed(1) : ''),
    render: (v) => (v.cr_grade != null ? v.cr_grade.toFixed(1) : '–'),
    numeric: true,
  },
  {
    key: 'days_since_inspection',
    label: 'Insp d',
    sortValue: (v) => v.days_since_inspection,
    searchValue: (v) => (v.days_since_inspection != null ? String(v.days_since_inspection) : ''),
    render: (v) => v.days_since_inspection ?? '–',
    numeric: true,
  },
  {
    key: 'list_days',
    label: 'List d',
    sortValue: (v) => v.list_days,
    searchValue: (v) => (v.list_days != null ? String(v.list_days) : ''),
    render: (v) => v.list_days ?? '–',
    numeric: true,
  },
  {
    key: 'listing_occurences',
    label: 'List O',
    sortValue: (v) => v.listing_occurences,
    searchValue: (v) => (v.listing_occurences != null ? String(v.listing_occurences) : ''),
    render: (v) => v.listing_occurences ?? '–',
    numeric: true,
  },
  {
    key: 'days_expired',
    label: 'Exp d',
    sortValue: (v) => v.days_expired,
    searchValue: (v) => (v.days_expired != null ? String(v.days_expired) : ''),
    render: (v) => v.days_expired ?? '–',
    numeric: true,
  },
  {
    key: 'vin_action_filter',
    label: 'Action',
    sortValue: (v) => (vinActionDisplay(v.vin_action_filter) || '').toLowerCase(),
    searchValue: (v) => vinActionDisplay(v.vin_action_filter) || '',
    render: (v) => vinActionDisplay(v.vin_action_filter) || '–',
  },
];

const statusBadgeClass = (status: string | null | undefined): string => {
  switch (status) {
    case 'In Progress': return 'status-badge status-in-progress';
    case 'Done': return 'status-badge status-done';
    case 'Sold / NA': return 'status-badge status-sold-na';
    case 'Updated': return 'status-badge status-updated';
    case 'Reinspection Needed': return 'status-badge status-reinspection';
    case 'Delete': return 'status-badge status-deleted';
    case 'CR Reuse': return 'status-badge status-cr-reuse';
    case 'For Auction': return 'status-badge status-for-auction';
    case 'Client Elect, No List': return 'status-badge status-client-elect';
    default: return 'status-badge status-unassigned';
  }
};

function compareSortValues(a: string | number | null | undefined, b: string | number | null | undefined, dir: SortDir): number {
  // Nulls sort last regardless of direction.
  const aNull = a == null || (typeof a === 'number' && Number.isNaN(a));
  const bNull = b == null || (typeof b === 'number' && Number.isNaN(b));
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  let cmp = 0;
  if (typeof a === 'number' && typeof b === 'number') {
    cmp = a - b;
  } else {
    cmp = String(a).localeCompare(String(b));
  }
  return dir === 'asc' ? cmp : -cmp;
}

const fmtInt = (n?: number | null) => (n == null ? '–' : Math.round(n).toLocaleString());
const fmtPct = (n?: number | null) => (n == null ? '–' : `${(n * 100).toFixed(1)}%`);

type Delta = { dir: 'up' | 'down' | 'flat'; pct?: number };

const computeDelta = (curr: number, prior: number): Delta => {
  if (prior === 0 || prior == null) return { dir: 'flat' };
  const diff = curr - prior;
  if (diff === 0) return { dir: 'flat' };
  const pct = (diff / Math.abs(prior)) * 100;
  return { dir: diff > 0 ? 'up' : 'down', pct: Math.abs(pct) };
};

// `invert` flips the good/bad coloring (e.g. fewer days-to-sell is better).
const DeltaBadge: React.FC<{ delta: Delta; invert?: boolean }> = ({ delta, invert }) => {
  if (delta.dir === 'flat') return <span className="cv-delta cv-delta-flat">—</span>;
  const arrow = delta.dir === 'up' ? '▲' : '▼';
  const good = invert ? delta.dir === 'down' : delta.dir === 'up';
  return (
    <span className={`cv-delta cv-delta-${good ? 'up' : 'down'}`}>
      {arrow}{delta.pct != null ? ` ${delta.pct.toFixed(0)}%` : ''}
    </span>
  );
};

// Core Client thresholds, mirrored from the upstream task so the panel can show
// the bar alongside the value.
const CORE_MIN_INSPECTIONS = 18;
const CORE_MIN_CONVERSION = 0.25;

// One definition per Core measure, shared by the strip, the grain table and the
// explainer so the three can never describe the same number differently.
const CORE_INSPECTIONS_DEF =
  'Inspections counted the way reporting and comp count them: one per vehicle, per dealer, per calendar month. Re-inspecting the same car in the same month does not count again.';
const CORE_NET_SALES_DEF =
  'Net sales (after back-outs) closing in the window on any vehicle this dealer had MMI-inspected before. The sale does not have to come from an inspection in the same window.';
const CORE_CONVERSION_DEF =
  'Net sales ÷ inspections, both counted in the same window. Capped at 100%.';

type SectionId = 'identity' | 'core' | 'funnel' | 'salesforce' | 'vins';
const DEFAULT_OPEN_SECTIONS: SectionId[] = ['core', 'vins'];

/** A pop-out section with a collapsible header. The header row keeps whatever
 *  controls the section already had (grain toggles, channel tabs, bulk actions)
 *  to the right of the toggle, so nothing moves when a section is opened. */
const PopoutSection: React.FC<{
  id: SectionId;
  title: string;
  open: boolean;
  onToggle: (id: SectionId) => void;
  className?: string;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, title, open, onToggle, className, headerExtra, children }) => (
  <section className={`cv-popout-section${className ? ` ${className}` : ''}`}>
    <div className="cv-core-header">
      <button
        type="button"
        className="cv-section-toggle"
        aria-expanded={open}
        onClick={() => onToggle(id)}
      >
        <span className={`cv-section-chevron${open ? ' is-open' : ''}`} aria-hidden="true">▶</span>
        <h3>{title}</h3>
      </button>
      {open && headerExtra}
    </div>
    {open && children}
  </section>
);

// Grains shown before the "more" expander. Week/Month/Period are the day-to-day
// read; Quarter's numbers lead the Core strip above, so it sits with the rest.
const DEFAULT_GRAIN_KEYS = ['week', 'month', 'period'];

/**
 * Core Client as a status strip rather than a table row.
 *
 * Standing is only calculated on the 3-month windows, so in a 6-grain table the
 * Standing and To Core rows were blank in four of six columns. This shows the two
 * windows that actually carry it, with the requirements as progress bars so the
 * gap is readable without arithmetic.
 */
function CoreWindowPanel({ label, sub, stats, comparisons = [], primary }: {
  label: string;
  sub: string;
  stats?: CoreWindowStats;
  comparisons?: { label: string; stats?: CoreWindowStats }[];
  primary?: boolean;
}) {
  const inspections = stats?.total_inspections ?? 0;
  const conversion = stats?.mmi_conversion ?? 0;
  const isCore = stats?.core_status === 1;
  const available = stats?.core_status_available === true;
  const inspPct = Math.min(100, (inspections / CORE_MIN_INSPECTIONS) * 100);
  const convPct = Math.min(100, (conversion / CORE_MIN_CONVERSION) * 100);
  const deltas = comparisons.flatMap((c) => {
    const prior = c.stats?.mmi_conversion ?? null;
    if (prior == null || stats?.mmi_conversion == null) return [];
    return [{ label: c.label, value: stats.mmi_conversion - prior, prior }];
  });

  return (
    <div className={`cv-core-panel${primary ? ' is-primary' : ''}`}>
      <div className="cv-core-panel-head">
        <span className="cv-core-panel-label">
          {label} <span className="cv-core-panel-sub">{sub}</span>
        </span>
        {available ? (
          <span className={`cv-core-badge${isCore ? ' is-core' : ''}`}>
            {stats?.client_status || '–'}
          </span>
        ) : (
          <span className="cv-core-badge is-na" title="Core standing is not calculated on this window.">
            n/a
          </span>
        )}
      </div>

      <div className="cv-core-bars">
        <div className="cv-core-bar-row" title={`${inspections} of ${CORE_MIN_INSPECTIONS} inspections needed. ${CORE_INSPECTIONS_DEF}`}>
          <span className="cv-core-bar-label">Inspections</span>
          <span className="cv-core-bar-track">
            <span
              className={`cv-core-bar-fill${inspections >= CORE_MIN_INSPECTIONS ? ' is-met' : ''}`}
              style={{ width: `${inspPct}%` }}
            />
          </span>
          <span className="cv-core-bar-value">
            {fmtInt(inspections)}<span className="cv-core-bar-target">/{CORE_MIN_INSPECTIONS}</span>
          </span>
        </div>
        <div className="cv-core-bar-row" title={`${fmtPct(conversion)} against the ${fmtPct(CORE_MIN_CONVERSION)} requirement. ${CORE_CONVERSION_DEF}`}>
          <span className="cv-core-bar-label">Conversion</span>
          <span className="cv-core-bar-track">
            <span
              className={`cv-core-bar-fill${conversion >= CORE_MIN_CONVERSION ? ' is-met' : ''}`}
              style={{ width: `${convPct}%` }}
            />
          </span>
          <span className="cv-core-bar-value">
            {fmtPct(conversion)}<span className="cv-core-bar-target">/{fmtPct(CORE_MIN_CONVERSION)}</span>
          </span>
        </div>
      </div>

      <div className="cv-core-panel-foot">
        <span title={CORE_NET_SALES_DEF}>
          {fmtInt(stats?.net_sales ?? 0)} net sales · {stats?.start_date || '–'} → {stats?.end_date || '–'}
        </span>
        {deltas.map((d) => (
          <span key={d.label} className={`cv-core-panel-delta${d.value >= 0 ? ' is-up' : ' is-down'}`}>
            {d.value >= 0 ? '▲' : '▼'} {fmtPct(Math.abs(d.value))} {d.label}
            {/* The prior reading itself, so the swing can be judged against its base. */}
            <span className="cv-core-panel-delta-prior"> (was {fmtPct(d.prior)})</span>
          </span>
        ))}
        {available && !isCore && (
          <span className="cv-core-panel-need">
            To Core: {coreNeedLabel(stats?.inspections_needed_for_core)} insp ·{' '}
            {coreNeedLabel(stats?.net_sales_needed_for_core)} sales
          </span>
        )}
      </div>
    </div>
  );
}

/** A metric table over the visible grains. One per funnel bucket so Inspections
 *  and Listings sit side by side rather than in one long combined table. */
function MetricTable<S>({ title, accent, cols, metrics, headerExtra }: {
  title: string;
  accent: 'inspections' | 'listings' | 'core';
  cols: { key: string; label: string; sub: string; tooltip: string; curr: S; comparisons: { label: string; stats: S }[] }[];
  metrics: KpiMetric<S>[];
  headerExtra?: React.ReactNode;
}) {
  return (
    <div className={`cv-kpi-card cv-kpi-card-${accent}`}>
      <div className="cv-kpi-card-head">
        <h4 className="cv-kpi-card-title">{title}</h4>
        {headerExtra}
      </div>
      <div className="cv-table-scroll">
        <table className="cv-kpi-table">
          <thead>
            <tr>
              <th className="cv-kpi-th-metric" />
              {cols.map((g) => (
                <th key={g.key} className="cv-kpi-th-grain" title={g.tooltip}>
                  {g.label}
                  <span className="cv-core-th-sub">{g.sub}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <MetricRow key={m.label} metric={m} cols={cols} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** One metric row across the visible grains. Shared by the Inspections and
 *  Listings groups so both sit under a single grain header. */
function MetricRow<S>({ metric, cols }: {
  metric: KpiMetric<S>;
  cols: { key: string; curr: S; comparisons: { label: string; stats: S }[] }[];
}) {
  return (
    <tr>
      <td className="cv-kpi-metric-label" title={metric.tooltip}>{metric.label}</td>
      {cols.map((g) => (
        <td key={g.key}>
          <MetricCell
            curr={metric.value(g.curr)}
            comparisons={g.comparisons.map((c) => ({
              label: c.label,
              prior: metric.value(c.stats),
            }))}
            format={metric.format}
            invert={metric.invert}
          />
        </td>
      ))}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// KPI card primitives (Inspections / Listings)
// ---------------------------------------------------------------------------

// One column per grain, with its comparison windows stacked inside each cell
// rather than spread across extra columns. Order runs shortest span to longest.
type GrainColumn = {
  key: string;
  label: string;
  sub: string;
  curr: ClientStatsWindowKey;
  comparisons: { label: string; window: ClientStatsWindowKey }[];
  // Distance-to-Core only means something on the window currently in flight.
  showToCore?: boolean;
  tooltip: string;
};
const GRAIN_COLUMNS: GrainColumn[] = [
  // The build runs overnight, so today is always empty — the day read is anchored
  // on yesterday and compared against the day before.
  { key: 'day', label: 'Day', sub: 'yesterday', curr: 'prior_day',
    comparisons: [{ label: 'vs prev day', window: 'prior_day_2' }],
    tooltip: 'Yesterday vs the day before. Today is excluded — the stats build runs overnight, so it is always empty.' },
  { key: 'week', label: 'Week', sub: 'to date', curr: 'week',
    comparisons: [{ label: 'vs prior wk', window: 'prior_week' }],
    tooltip: 'This week (Mon through today) vs the last full week' },
  { key: 'month', label: 'Month', sub: 'to date', curr: 'month',
    comparisons: [{ label: 'vs prior mo', window: 'prior_month' }],
    tooltip: 'This month to date vs the last full month' },
  { key: 'quarter', label: 'Quarter', sub: 'to date', curr: 'quarter',
    comparisons: [
      { label: 'vs prior Q', window: 'prior_quarter' },
      { label: 'vs LY', window: 'quarter_last_year' },
    ],
    showToCore: true,
    tooltip: 'Calendar quarter to date against the last full quarter and the same quarter last year' },
  { key: 'period', label: 'Period', sub: 'rolling 3mo', curr: 'period',
    comparisons: [{ label: 'vs prior', window: 'prior_period' }],
    showToCore: true,
    tooltip: 'Rolling 3 months through today vs the 3 months before it — the day-to-day read' },
  { key: 'ytd', label: 'YTD', sub: 'to date', curr: 'ytd',
    comparisons: [{ label: 'vs last YTD', window: 'prior_ytd' }],
    tooltip: 'Jan 1 through the end of this month vs the same span last year' },
];

// The window maps are partial — the payload carries whatever windows the source
// table holds — so every lookup falls back to a zeroed block.
const EMPTY_INSP: InspectionStats = {
  total_inspections: 0,
  inspections_that_listed: 0,
  inspections_that_sold: 0,
};
const EMPTY_LST: ListingStats = { total_listings: 0, total_sales: 0 };
const EMPTY_CORE: CoreWindowStats = { total_inspections: 0, net_sales: 0, gross_sales: 0 };
const EMPTY_LST_CHANNELS: ListingChannelStats = {
  total: EMPTY_LST,
  live: EMPTY_LST,
  timed: EMPTY_LST,
};

const inspConversion = (s: InspectionStats): number | null =>
  s.total_inspections > 0 ? s.inspections_that_sold / s.total_inspections : null;
const lstConversion = (s: ListingStats): number | null =>
  s.total_listings > 0 ? s.total_sales / s.total_listings : null;
const mmrRetention = (s: { avg_purchase_price?: number | null; avg_sold_mmr?: number | null }): number | null =>
  s.avg_purchase_price != null && s.avg_sold_mmr != null && s.avg_sold_mmr !== 0
    ? s.avg_purchase_price / s.avg_sold_mmr
    : null;

interface KpiMetric<S> {
  label: string;
  tooltip?: string;
  value: (s: S) => number | null;
  format: (n?: number | null) => string;
  invert?: boolean;
}

const MetricCell: React.FC<{
  curr: number | null;
  comparisons: { label: string; prior: number | null }[];
  format: (n?: number | null) => string;
  invert?: boolean;
}> = ({ curr, comparisons, format, invert }) => (
  <div className="cv-kpi-cell">
    <span className="cv-kpi-cell-val">{format(curr)}</span>
    {comparisons.map((c) => {
      // Treat values that render identically (e.g. both "0", both "0.0%") as no
      // change, so rounding never produces a misleading 100% swing.
      const delta =
        curr != null && c.prior != null && format(curr) !== format(c.prior)
          ? computeDelta(curr, c.prior)
          : { dir: 'flat' as const };
      return (
        <span key={c.label} className="cv-kpi-cell-cmp">
          <span className="cv-kpi-cell-cmp-label">{c.label}</span>
          <DeltaBadge delta={delta} invert={invert} />
          {/* The percentage alone hides scale: 2 -> 3 and 200 -> 300 both read
              "+50%". Show what it moved FROM. */}
          <span className="cv-kpi-cell-cmp-prior" title={`Prior value: ${format(c.prior)}`}>
            {format(c.prior)}
          </span>
        </span>
      );
    })}
  </div>
);

// Core measures across every grain. Standing and To Core are deliberately absent:
// they only exist on the 3-month windows, and live on the strip above instead.
const CORE_METRICS: KpiMetric<CoreWindowStats>[] = [
  { label: 'Inspections', value: (s) => s.total_inspections, format: fmtInt,
    tooltip: `${CORE_INSPECTIONS_DEF} Day and week show the vehicles that first counted that day or week, so the weeks of a month add up to the month. Core needs ${CORE_MIN_INSPECTIONS}+.` },
  { label: 'Net Sales', value: (s) => s.net_sales, format: fmtInt,
    tooltip: `${CORE_NET_SALES_DEF} The conversion numerator.` },
  { label: 'MMI Conversion', value: (s) => s.mmi_conversion ?? null, format: fmtPct,
    tooltip: `${CORE_CONVERSION_DEF} Core needs ${fmtPct(CORE_MIN_CONVERSION)}+.` },
];

const INSPECTION_METRICS: KpiMetric<InspectionStats>[] = [
  { label: 'Inspection events', value: (s) => s.total_inspections, format: fmtInt,
    tooltip: 'Every MMI inspection in the window, including re-inspections of the same vehicle (repeats within 3 days are merged). Can read higher than Core Client inspections, which count each vehicle once per month.' },
  { label: 'Sold', value: (s) => s.inspections_that_sold, format: fmtInt,
    tooltip: 'Of those inspections, how many went on to sell, whenever the sale happened. The numerator of Inspection Conv. Not the same as Core Net Sales.' },
  { label: 'Inspection Conv.', value: inspConversion, format: fmtPct,
    tooltip: 'Sold ÷ inspection events. A cohort rate: recent windows read low until their sales land. Does not decide Core standing.' },
  { label: '% MMR Retention', value: mmrRetention, format: fmtPct,
    tooltip: 'Avg purchase price ÷ avg sold MMR. Closer to 100% = sold near market.' },
  { label: 'Avg Days to List', value: (s) => s.avg_days_inspection_to_offering ?? null,
    format: fmtInt, invert: true,
    tooltip: 'Avg days from inspection to the first offering appearing. Lower is better.' },
  { label: 'Avg Days to Sell', value: (s) => s.avg_days_inspection_to_sell ?? null,
    format: fmtInt, invert: true,
    tooltip: 'Avg days from inspection to sale. Lower is better.' },
];

const LISTING_METRICS: KpiMetric<ListingStats>[] = [
  { label: 'Listings', value: (s) => s.total_listings, format: fmtInt,
    tooltip: 'Count of VINs listed at auction in the window.' },
  { label: 'Sales', value: (s) => s.total_sales, format: fmtInt,
    tooltip: 'Listed VINs that sold — the numerator of Listing Conv.' },
  { label: 'Listing Conv.', value: lstConversion, format: fmtPct,
    tooltip: 'Sales ÷ listings.' },
  { label: '% MMR Retention', value: mmrRetention, format: fmtPct,
    tooltip: 'Avg purchase price ÷ avg sold MMR. Closer to 100% = sold near market.' },
  { label: 'Avg Days to Sell', value: (s) => s.avg_days_offering_to_sell ?? null,
    format: fmtInt, invert: true,
    tooltip: 'Avg days from the first offering to sale — the whole sell cycle, including re-lists. Lower is better.' },
];


export const DealerInsightsPopout: React.FC<DealerInsightsPopoutProps> = ({
  sellerId, onClose, onVinClick, miaEnabled = false, onContinueInMia, fullScreen = false,
}) => {
  useEffect(() => {
    track('client_view', 'client_popout', 'open', { seller_uid: sellerId });
    return pushUsageContext('client_popout', { seller_uid: sellerId });
  }, [sellerId]);
  const [detail, setDetail] = useState<ClientViewDealerDetail | null>(null);
  const [vins, setVins] = useState<ClientViewVinRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vinSearch, setVinSearch] = useState('');
  const [vinFilter, setVinFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortEntry[]>([]);
  const [selectedVins, setSelectedVins] = useState<Set<string>>(new Set());
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  // RDS-2.3: free-text reason per VIN status, loaded for the rows that have a status.
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [bulkReason, setBulkReason] = useState('');
  const [reasonEditor, setReasonEditor] = useState<{ vins: string[]; status: string; draft: string } | null>(null);
  const [copiedVins, setCopiedVins] = useState(false);
  const [copiedVinRows, setCopiedVinRows] = useState(false);
  const [copiedSeller, setCopiedSeller] = useState(false);
  const [listingChannel, setListingChannel] = useState<'total' | 'live' | 'timed'>('total');
  const [showAllCoreGrains, setShowAllCoreGrains] = useState(false);
  const [showAllFunnelGrains, setShowAllFunnelGrains] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  // Core Client is the decision and VINs is the work; everything else is
  // supporting evidence, so the pop-out opens on those two rather than on a
  // five-section scroll.
  const [openSections, setOpenSections] = useState<Set<SectionId>>(
    () => new Set<SectionId>(DEFAULT_OPEN_SECTIONS),
  );
  const toggleSection = useCallback((id: SectionId) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Reasons ride on the status record, not the VIN row; fetch them for rows that have a status.
  const loadReasons = useCallback(async (rows: ClientViewVinRow[], isCancelled: () => boolean) => {
    const withStatus = rows.filter((r) => r.user_status).map((r) => r.vin);
    if (withStatus.length === 0) {
      if (!isCancelled()) setReasons({});
      return;
    }
    try {
      const details = await siftApiClient.getVinStatusDetails(withStatus);
      if (isCancelled()) return;
      const next: Record<string, string> = {};
      for (const [vin, d] of Object.entries(details)) if (d.reason_text) next[vin] = d.reason_text;
      setReasons(next);
    } catch (err) {
      console.error('Failed to load VIN status reasons', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [d, v] = await Promise.all([
          siftApiClient.getClientViewDealerDetail(sellerId),
          siftApiClient.getClientViewDealerVins(sellerId),
        ]);
        if (cancelled) return;
        setDetail(d);
        setVins(v.vins);
        void loadReasons(v.vins, () => cancelled);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || 'Failed to load dealer detail');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [sellerId, loadReasons]);

  // Re-fetch only the VIN rows when the deleted toggle flips, so the whole
  // pop-out doesn't drop back to its loading state for a table-level control.
  const firstVinLoad = useRef(true);
  useEffect(() => {
    if (firstVinLoad.current) {
      firstVinLoad.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const v = await siftApiClient.getClientViewDealerVins(sellerId, showDeleted);
        if (!cancelled) setVins(v.vins);
        void loadReasons(v.vins, () => cancelled);
      } catch (err) {
        console.error('Failed to reload dealer VINs', err);
      }
    })();
    return () => { cancelled = true; };
  }, [sellerId, showDeleted, loadReasons]);

  const vinBuckets = useMemo(() => {
    const buckets: Record<string, number> = { all: vins.length };
    for (const v of vins) {
      const key = v.vin_action_filter || 'Unknown';
      buckets[key] = (buckets[key] || 0) + 1;
    }
    return buckets;
  }, [vins]);

  const statusBuckets = useMemo(() => {
    const buckets: Record<string, number> = { all: vins.length };
    for (const v of vins) {
      const key = v.user_status || USER_STATUS_DISPLAY_DEFAULT;
      buckets[key] = (buckets[key] || 0) + 1;
    }
    return buckets;
  }, [vins]);

  const filteredVins = useMemo(() => {
    let arr = vins;
    if (vinFilter !== 'all') {
      arr = arr.filter((v) => (v.vin_action_filter || 'Unknown') === vinFilter);
    }
    if (statusFilter !== 'all') {
      arr = arr.filter((v) => (v.user_status || USER_STATUS_DISPLAY_DEFAULT) === statusFilter);
    }
    if (vinSearch.trim()) {
      const q = vinSearch.trim().toLowerCase();
      arr = arr.filter((v) =>
        VIN_COLUMNS.some((col) => col.searchValue(v).toLowerCase().includes(q))
      );
    }
    if (sortBy.length > 0) {
      const colMap = new Map(VIN_COLUMNS.map((c) => [c.key, c]));
      arr = [...arr].sort((a, b) => {
        for (const s of sortBy) {
          const col = colMap.get(s.key);
          if (!col) continue;
          const cmp = compareSortValues(col.sortValue(a), col.sortValue(b), s.dir);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
    }
    return arr;
  }, [vins, vinFilter, statusFilter, vinSearch, sortBy]);

  // Inline edit of a single VIN's user_status (optimistic).
  const handleStatusChange = async (vin: string, next: string) => {
    const value: UserVinStatus = next === USER_STATUS_DISPLAY_DEFAULT ? null : (next as UserVinStatus);
    const prevVins = vins;
    const prevReasons = reasons;
    setVins((arr) => arr.map((r) => (r.vin === vin ? { ...r, user_status: value } : r)));
    // A new status replaces the reason (the backend clears it too).
    setReasons(({ [vin]: _dropped, ...rest }) => rest);
    try {
      if (value === null) {
        await siftApiClient.deleteVinStatus(vin);
      } else {
        await siftApiClient.setVinStatus(vin, value);
        // The reason is optional; offer it straight away for the status that most needs one.
        if (value === CLIENT_ELECT_NO_LIST) setReasonEditor({ vins: [vin], status: value, draft: '' });
      }
    } catch (err) {
      console.error('Failed to update VIN status', err);
      setVins(prevVins);
      setReasons(prevReasons);
    }
  };

  const saveReason = async () => {
    if (!reasonEditor) return;
    const { vins: targets, status, draft } = reasonEditor;
    const text = draft.trim();
    setReasonEditor(null);
    const prevReasons = reasons;
    setReasons((prev) => {
      const next = { ...prev };
      for (const v of targets) {
        if (text) next[v] = text; else delete next[v];
      }
      return next;
    });
    try {
      if (targets.length === 1) await siftApiClient.setVinStatus(targets[0], status, text || null);
      else await siftApiClient.setVinStatusBulk(targets, status, text || null);
    } catch (err) {
      console.error('Failed to save VIN status reason', err);
      setReasons(prevReasons);
    }
  };

  // Bulk-assign status to all selected VINs (optimistic).
  const handleBulkStatusAssign = async (status: UserVinStatus) => {
    const vinList = Array.from(selectedVins);
    if (vinList.length === 0) {
      setShowBulkStatusModal(false);
      return;
    }
    const prevVins = vins;
    const prevReasons = reasons;
    const targetSet = new Set(vinList);
    const reasonText = status === null ? '' : bulkReason.trim();
    setVins((arr) => arr.map((r) => (targetSet.has(r.vin) ? { ...r, user_status: status } : r)));
    setReasons((prev) => {
      const next = { ...prev };
      for (const v of vinList) {
        if (reasonText) next[v] = reasonText; else delete next[v];
      }
      return next;
    });
    setShowBulkStatusModal(false);
    setBulkReason('');
    try {
      if (status === null) {
        await siftApiClient.deleteVinStatusBulk(vinList);
      } else {
        await siftApiClient.setVinStatusBulk(vinList, status, reasonText || null);
      }
      setSelectedVins(new Set());
    } catch (err) {
      console.error('Failed to bulk update VIN status', err);
      setVins(prevVins);
      setReasons(prevReasons);
    }
  };

  const toggleVinSelected = (vin: string) => {
    setSelectedVins((prev) => {
      const next = new Set(prev);
      if (next.has(vin)) next.delete(vin); else next.add(vin);
      return next;
    });
  };

  // Copy the selected VINs (newline-separated) to the clipboard.
  const handleCopyVins = async () => {
    const vinList = Array.from(selectedVins);
    if (vinList.length === 0) return;
    try {
      await navigator.clipboard.writeText(vinList.join('\n'));
      setCopiedVins(true);
      setTimeout(() => setCopiedVins(false), 1500);
    } catch (err) {
      console.error('Failed to copy VINs to clipboard', err);
    }
  };

  // Same selection, but VIN + year/make/model/trim tab-separated so it pastes into
  // two spreadsheet columns. Rows follow the table's current sort.
  const handleCopyVinsWithVehicle = async () => {
    const rows = filteredVins
      .filter((v) => selectedVins.has(v.vin))
      .map((v) => `${v.vin}\t${ymmt(v)}`);
    if (rows.length === 0) return;
    try {
      await navigator.clipboard.writeText(rows.join('\n'));
      setCopiedVinRows(true);
      setTimeout(() => setCopiedVinRows(false), 1500);
    } catch (err) {
      console.error('Failed to copy VIN rows to clipboard', err);
    }
  };

  const handleSortClick = (key: SortKey, e: React.MouseEvent) => {
    const additive = e.shiftKey;
    setSortBy((prev) => {
      const existingIdx = prev.findIndex((s) => s.key === key);
      if (additive) {
        if (existingIdx === -1) return [...prev, { key, dir: 'asc' }];
        const existing = prev[existingIdx];
        if (existing.dir === 'asc') {
          const next = [...prev];
          next[existingIdx] = { key, dir: 'desc' };
          return next;
        }
        // 3rd click on a shift-sorted column removes it.
        return prev.filter((_, i) => i !== existingIdx);
      }
      // Non-additive: replace entire sort with just this column, toggling direction.
      if (existingIdx !== -1 && prev.length === 1) {
        const existing = prev[existingIdx];
        if (existing.dir === 'asc') return [{ key, dir: 'desc' }];
        return []; // 3rd click clears
      }
      return [{ key, dir: 'asc' }];
    });
  };

  const tier = detail ? classifyTier(detail.core) : 'inactive';

  const grainsFor = (all: boolean) => (all
    ? GRAIN_COLUMNS
    : GRAIN_COLUMNS.filter((g) => DEFAULT_GRAIN_KEYS.includes(g.key)));
  const coreGrains = grainsFor(showAllCoreGrains);
  const funnelGrains = grainsFor(showAllFunnelGrains);
  // notes[0] IS the last contact (same ranked_tasks CTE, rn=1), so it is rendered
  // as the headline instead of twice. Only drop it when it really is the same task.
  const allNotes = detail?.recent_notes ?? [];
  const earlierNotes = allNotes.length > 0 && allNotes[0].date === detail?.sfdc.last_contact_date
    ? allNotes.slice(1)
    : allNotes;
  // Each section owns its own expander -- one shared control silently changed the
  // other section's columns too.
  const grainToggle = (all: boolean, set: (fn: (v: boolean) => boolean) => void) => (
    <button
      type="button"
      className="cv-grain-toggle"
      onClick={() => set((v) => !v)}
      aria-expanded={all}
      title={all
        ? 'Show only Week, Month and Period'
        : 'Also show Day, Quarter and YTD'}
    >
      {all ? '− Fewer grains' : '+ Day · Quarter · YTD'}
    </button>
  );
  // Quarter columns run quarter-to-DATE, so the 18-inspection requirement is not
  // yet reachable early in a quarter and every dealer reads short.
  const quarterProgress = quarterElapsed(
    detail?.core_windows?.quarter?.start_date,
    detail?.core_windows?.quarter?.end_date,
  );

  return (
    <div
      className={`cv-popout-panel${fullScreen ? ' cv-popout-panel--full' : ''}`}
      role="dialog"
      aria-label="Dealer insights"
      aria-modal={fullScreen || undefined}
    >
        <div className="cv-popout-header">
          <div className="cv-popout-title">
            {fullScreen && (
              <button type="button" className="cv-popout-back" onClick={onClose}>
                ‹ Dealers
              </button>
            )}
            <h2>{detail?.seller_name || 'Loading...'}</h2>
            {detail && (
              <span className="cv-popout-tier" style={{ background: TIER_COLOR[tier] }}>
                {TIER_LABEL[tier]}
              </span>
            )}
            {detail && (detail.core.period_start_date || detail.core.period_end_date) && (
              <span
                className="cv-popout-core-period"
                title="Reporting period window for this dealer"
              >
                {detail.core.period_start_date || '–'} → {detail.core.period_end_date || '–'}
              </span>
            )}
            <a
              className="cv-popout-sellcenter-link"
              href="https://sellcenter.manheim.com/admin"
              target="_blank"
              rel="noopener noreferrer"
              title={`Open Manheim Selling Center (copies seller # ${sellerId} to clipboard)`}
              onClick={() => {
                navigator.clipboard.writeText(sellerId).then(() => {
                  setCopiedSeller(true);
                  setTimeout(() => setCopiedSeller(false), 1500);
                }).catch((err) => console.error('Failed to copy seller number', err));
              }}
            >
              {copiedSeller ? 'Seller # copied!' : 'Selling Center ↗'}
            </a>
            {detail?.salesforce_id && (
              <a
                className="cv-popout-salesforce-link"
                href={`https://manheim.lightning.force.com/lightning/r/Account/${detail.salesforce_id}/view`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open client profile in Salesforce"
              >
                Salesforce ↗
              </a>
            )}
          </div>
          <button className="cv-popout-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {loading && <div className="cv-popout-loading">Loading dealer details...</div>}
        {error && <div className="cv-popout-error">{error}</div>}

        {detail && !loading && (
          <div className="cv-popout-body">
            {miaEnabled && (
              <RdsMiaSummary key={sellerId} kind="client" id={sellerId} onContinue={onContinueInMia} />
            )}
            {/* Identity block */}
            <PopoutSection
              id="identity"
              title="Client Details"
              open={openSections.has('identity')}
              onToggle={toggleSection}
            >
              <div className="cv-identity-grid">
                {/* Parent first: it frames who the dealer belongs to before its own
                    id. 'Independent' is the real answer for most clients — upstream
                    marks them with a sentinel that is suppressed server-side. */}
                <div>
                  <span className="cv-identity-label">Parent Group</span>
                  <span>{detail.client_parent_name || 'Independent'}</span>
                </div>
                <div><span className="cv-identity-label">Seller ID</span><span>{detail.seller_id}</span></div>
                <div><span className="cv-identity-label">Segmentation</span><span>{detail.seller_segmentation || '–'}</span></div>
                <div><span className="cv-identity-label">Director</span><span>{detail.director || '–'}</span></div>
                <div><span className="cv-identity-label">Manager</span><span>{detail.manager || '–'}</span></div>
                <div><span className="cv-identity-label">RDS Territory</span><span>{detail.rds_territory || '–'}</span></div>
                <div><span className="cv-identity-label">CSE</span><span>{detail.cse_territory || '–'}</span></div>
                <div><span className="cv-identity-label">Location</span><span>{[detail.territory_city, detail.territory_state].filter(Boolean).join(', ')} {detail.territory_zip}</span></div>
              </div>
            </PopoutSection>

            {/* Core Client — the decision. A strip, not a table: standing exists on only
                the 3-month windows, so four of six grain columns were always blank. */}
            <PopoutSection
              id="core"
              title="Core Client"
              open={openSections.has('core')}
              onToggle={toggleSection}
              className="cv-core-progress"
            >
              <p className="cv-core-explainer">
                <strong>Inspections</strong> count one per vehicle, per dealer, per calendar
                month, the same way reporting and comp count them.{' '}
                <strong>Net sales</strong> are sales closing in the window on any vehicle the
                dealer had MMI-inspected before. <strong>Conversion</strong> is net sales ÷
                inspections, both counted in the window. Core requires{' '}
                <strong>{CORE_MIN_INSPECTIONS}+ inspections</strong> and{' '}
                <strong>{fmtPct(CORE_MIN_CONVERSION)} conversion</strong>, so it is only
                calculated on the 3-month spans below.
              </p>
              {quarterProgress && (
                <p className="cv-core-warning">
                  Quarter is to date — day {quarterProgress.elapsed} of {quarterProgress.total}.
                  Volume is still accumulating, so the {CORE_MIN_INSPECTIONS}-inspection
                  requirement (and any standing derived from it) reads short until the
                  quarter matures.
                </p>
              )}
              <div className="cv-core-strip">
                <CoreWindowPanel
                  primary
                  label="Period"
                  sub="rolling 3 months"
                  stats={detail.core_windows?.period}
                  comparisons={[
                    { label: 'vs prior', stats: detail.core_windows?.prior_period },
                  ]}
                />
                <CoreWindowPanel
                  label="Quarter"
                  sub="to date"
                  stats={detail.core_windows?.quarter}
                  comparisons={[
                    { label: 'vs prior Q', stats: detail.core_windows?.prior_quarter },
                    { label: 'vs LY', stats: detail.core_windows?.quarter_last_year },
                  ]}
                />
              </div>

              <MetricTable<CoreWindowStats>
                title="By grain"
                accent="core"
                cols={coreGrains.map((g) => ({
                  key: g.key,
                  label: g.label,
                  sub: g.sub,
                  tooltip: g.tooltip,
                  curr: detail.core_windows?.[g.curr] ?? EMPTY_CORE,
                  comparisons: g.comparisons.map((c) => ({
                    label: c.label,
                    stats: detail.core_windows?.[c.window] ?? EMPTY_CORE,
                  })),
                }))}
                metrics={CORE_METRICS}
                headerExtra={grainToggle(showAllCoreGrains, setShowAllCoreGrains)}
              />
            </PopoutSection>

            {/* Funnel — a DIFFERENT question from the Core measure above. */}
            <PopoutSection
              id="funnel"
              title="Inspection &amp; Listing Funnel"
              open={openSections.has('funnel')}
              onToggle={toggleSection}
              headerExtra={grainToggle(showAllFunnelGrains, setShowAllFunnelGrains)}
            >
              <p className="cv-core-explainer">
                Cohort view: of the inspections done in a window, how many went on to list and
                sell, whenever that happened. Useful for diagnosing where units stall. This
                counts every <strong>inspection event</strong>, so a vehicle inspected twice
                in a month counts twice here but once in Core Client.
                <strong> Does not drive Core standing</strong>, which is measured in-period above.
              </p>
              <div className="cv-kpi-cards">
                <MetricTable<InspectionStats>
                  title="Inspections"
                  accent="inspections"
                  cols={funnelGrains.map((g) => ({
                    key: g.key,
                    label: g.label,
                    sub: g.sub,
                    tooltip: g.tooltip,
                    curr: detail.inspections[g.curr] ?? EMPTY_INSP,
                    comparisons: g.comparisons.map((c) => ({
                      label: c.label,
                      stats: detail.inspections[c.window] ?? EMPTY_INSP,
                    })),
                  }))}
                  metrics={INSPECTION_METRICS}
                />
                <MetricTable<ListingStats>
                  title="Listings"
                  accent="listings"
                  cols={funnelGrains.map((g) => ({
                    key: g.key,
                    label: g.label,
                    sub: g.sub,
                    tooltip: g.tooltip,
                    curr: (detail.listings[g.curr] ?? EMPTY_LST_CHANNELS)[listingChannel],
                    comparisons: g.comparisons.map((c) => ({
                      label: c.label,
                      stats: (detail.listings[c.window] ?? EMPTY_LST_CHANNELS)[listingChannel],
                    })),
                  }))}
                  metrics={LISTING_METRICS}
                  headerExtra={
                    <div className="cv-kpi-channel-toggle" role="tablist" aria-label="Listing channel">
                      {(['total', 'live', 'timed'] as const).map((ch) => (
                        <button
                          key={ch}
                          type="button"
                          role="tab"
                          aria-selected={listingChannel === ch}
                          className={`cv-kpi-channel-btn${listingChannel === ch ? ' is-active' : ''}`}
                          onClick={() => setListingChannel(ch)}
                        >
                          {ch === 'total' ? 'Total' : ch === 'live' ? 'Live' : 'Timed'}
                        </button>
                      ))}
                    </div>
                  }
                />
              </div>
            </PopoutSection>

            {/* Salesforce engagement. One timeline, not two columns: recent_notes is
                the last 5 tasks and last_contact_* is that same set at rn=1, so the old
                "Last Contact" panel and the first note were the same task twice. */}
            <PopoutSection
              id="salesforce"
              title="Salesforce Activity"
              open={openSections.has('salesforce')}
              onToggle={toggleSection}
              headerExtra={
                <div className="cv-engage-counts" aria-label="Contact counts by window">
                  {([
                    ['7d', detail.sfdc.sfdc_contact_count_7d],
                    ['30d', detail.sfdc.sfdc_contact_count_30d],
                    ['90d', detail.sfdc.sfdc_contact_count_90d],
                    ['13mo', detail.sfdc.sfdc_contact_count_13mo],
                  ] as const).map(([label, count]) => (
                    <span key={label} className="cv-engage-count" title={`${count ?? 0} contacts in the last ${label}`}>
                      <span className="cv-engage-count-num">{count ?? 0}</span>
                      <span className="cv-engage-count-label">{label}</span>
                    </span>
                  ))}
                </div>
              }
            >

              {detail.sfdc.last_contact_date ? (
                <div className="cv-engage-timeline">
                  <div className="cv-engage-latest">
                    <div className="cv-engage-latest-head">
                      <span className="cv-engage-latest-when">
                        {detail.sfdc.days_since_last_contact != null
                          ? `${detail.sfdc.days_since_last_contact}d ago`
                          : 'Last contact'}
                      </span>
                      <span className="cv-engage-meta">
                        {[
                          detail.sfdc.last_contact_date,
                          detail.sfdc.last_contact_type,
                          detail.sfdc.last_contact_direction,
                          detail.sfdc.last_contact_status,
                          detail.sfdc.last_contact_agent,
                        ].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    {detail.sfdc.last_contact_subject && (
                      <div className="cv-engage-subject">{detail.sfdc.last_contact_subject}</div>
                    )}
                    {detail.sfdc.last_contact_description && (
                      <div className="cv-engage-desc">{detail.sfdc.last_contact_description}</div>
                    )}
                  </div>

                  {earlierNotes.length > 0 && (
                    <ul className="cv-notes-list">
                      {earlierNotes.map((n, i) => (
                        <li key={i} className="cv-note">
                          <span className="cv-note-date">{n.date || '–'}</span>
                          <span className="cv-note-body">
                            <span className="cv-note-subject">{n.subject || '(no subject)'}</span>
                            <span className="cv-note-meta">
                              {[n.type, n.direction, n.agent].filter(Boolean).join(' · ')}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="cv-engage-empty">No recorded contact</div>
              )}
            </PopoutSection>

            {/* VIN table */}
            <section className="cv-popout-section">
              <div className="cv-vins-header">
                <button
                  type="button"
                  className="cv-section-toggle"
                  aria-expanded={openSections.has('vins')}
                  onClick={() => toggleSection('vins')}
                >
                  <span className={`cv-section-chevron${openSections.has('vins') ? ' is-open' : ''}`} aria-hidden="true">▶</span>
                  <h3>VINs ({vins.length}){selectedVins.size > 0 && <span className="cv-vins-sel-count"> — {selectedVins.size} selected</span>}</h3>
                </button>
                <div className="cv-vins-controls">
                  {openSections.has('vins') && (
                    <>
                  {selectedVins.size > 0 && (
                    <>
                      <button
                        type="button"
                        className="cv-vins-bulk-btn"
                        onClick={() => setShowBulkStatusModal(true)}
                      >
                        Assign Status ({selectedVins.size})
                      </button>
                      <button
                        type="button"
                        className="cv-vins-bulk-btn"
                        onClick={handleCopyVins}
                      >
                        {copiedVins ? 'Copied!' : `Copy VINs (${selectedVins.size})`}
                      </button>
                      <button
                        type="button"
                        className="cv-vins-bulk-btn"
                        onClick={handleCopyVinsWithVehicle}
                        title="Copy VIN and year/make/model/trim as two tab-separated columns"
                      >
                        {copiedVinRows ? 'Copied!' : `Copy VIN + Vehicle (${selectedVins.size})`}
                      </button>
                      <button
                        type="button"
                        className="cv-vins-bulk-btn cv-vins-bulk-btn-secondary"
                        onClick={() => setSelectedVins(new Set())}
                      >
                        Clear
                      </button>
                    </>
                  )}
                  <input
                    type="text"
                    placeholder="Search all columns"
                    value={vinSearch}
                    onChange={(e) => setVinSearch(e.target.value)}
                    className="cv-vin-search"
                  />
                  <select className="cv-vin-filter" value={vinFilter} onChange={(e) => setVinFilter(e.target.value)}>
                    {Object.entries(vinBuckets).map(([k, c]) => (
                      <option key={k} value={k}>{k === 'all' ? 'All Actions' : vinActionDisplay(k)} ({c})</option>
                    ))}
                  </select>
                  <select className="cv-vin-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    {Object.entries(statusBuckets).map(([k, c]) => (
                      <option key={k} value={k}>{k === 'all' ? 'All Status' : k} ({c})</option>
                    ))}
                  </select>
                  {/* Deleted VINs are hidden everywhere else, so this is the way back. */}
                  <label
                    className="cv-vin-show-deleted"
                    title="VINs marked Delete are hidden from this table and from all counts. Tick to review them or set a different status."
                  >
                    <input
                      type="checkbox"
                      checked={showDeleted}
                      onChange={(e) => setShowDeleted(e.target.checked)}
                    />
                    Show deleted
                  </label>
                    </>
                  )}
                </div>
              </div>
              {openSections.has('vins') && (
              <div className="cv-vins-table-wrap">
                <table className="cv-vins-table">
                  <thead>
                    <tr>
                      <th
                        className="cv-vins-th cv-vins-th-check"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          aria-label="Select all visible VINs"
                          checked={
                            filteredVins.length > 0 &&
                            filteredVins.every((v) => selectedVins.has(v.vin))
                          }
                          ref={(el) => {
                            if (el) {
                              const selectedCount = filteredVins.filter((v) => selectedVins.has(v.vin)).length;
                              el.indeterminate = selectedCount > 0 && selectedCount < filteredVins.length;
                            }
                          }}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedVins((prev) => {
                              const next = new Set(prev);
                              if (checked) {
                                filteredVins.forEach((v) => next.add(v.vin));
                              } else {
                                filteredVins.forEach((v) => next.delete(v.vin));
                              }
                              return next;
                            });
                          }}
                        />
                      </th>
                      {VIN_COLUMNS.map((col) => {
                        const sortIdx = sortBy.findIndex((s) => s.key === col.key);
                        const sortEntry = sortIdx !== -1 ? sortBy[sortIdx] : null;
                        const arrow = sortEntry ? (sortEntry.dir === 'asc' ? '▲' : '▼') : '';
                        return (
                          <th
                            key={col.key}
                            className={`cv-vins-th${sortEntry ? ' cv-vins-th-sorted' : ''}${col.numeric ? ' cv-vins-th-num' : ''}`}
                            onClick={(e) => handleSortClick(col.key, e)}
                            title="Click to sort. Shift+click to add to multi-sort."
                          >
                            <span className="cv-vins-th-label">{col.label}</span>
                            {sortEntry && (
                              <span className="cv-vins-th-sort">
                                {arrow}
                                {sortBy.length > 1 && <sup>{sortIdx + 1}</sup>}
                              </span>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVins.length === 0 ? (
                      <tr><td colSpan={VIN_COLUMNS.length + 1} className="cv-vins-empty">No VINs match</td></tr>
                    ) : (
                      filteredVins.map((v) => (
                        <tr key={v.vin} className="cv-vin-row" onClick={() => onVinClick?.(v)} data-usage-vin={v.vin}>
                          <td
                            className="cv-vins-td-check"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              aria-label={`Select VIN ${v.vin}`}
                              checked={selectedVins.has(v.vin)}
                              onChange={() => toggleVinSelected(v.vin)}
                            />
                          </td>
                          {VIN_COLUMNS.map((col) => {
                            if (col.key === 'user_status') {
                              const current = v.user_status || USER_STATUS_DISPLAY_DEFAULT;
                              return (
                                <td
                                  key={col.key}
                                  className={col.cellClass}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <select
                                    className={`cv-vin-status-select ${statusBadgeClass(v.user_status)}`}
                                    value={current}
                                    onChange={(e) => handleStatusChange(v.vin, e.target.value)}
                                  >
                                    <option value={USER_STATUS_DISPLAY_DEFAULT}>{USER_STATUS_DISPLAY_DEFAULT}</option>
                                    {USER_STATUS_OPTIONS.filter(Boolean).map((s) => (
                                      <option key={s as string} value={s as string}>{s}</option>
                                    ))}
                                  </select>
                                  {v.user_status && (
                                    <button
                                      type="button"
                                      className={`cv-vin-reason-btn${reasons[v.vin] ? ' cv-vin-reason-btn-set' : ''}`}
                                      title={reasons[v.vin] ? `Reason: ${reasons[v.vin]}` : 'Add a reason (optional)'}
                                      aria-label={reasons[v.vin] ? `Edit reason for ${v.vin}` : `Add a reason for ${v.vin}`}
                                      onClick={() => setReasonEditor({
                                        vins: [v.vin], status: v.user_status as string, draft: reasons[v.vin] || '',
                                      })}
                                    >
                                      ✎
                                    </button>
                                  )}
                                </td>
                              );
                            }
                            return (
                              <td key={col.key} className={col.cellClass}>{col.render(v)}</td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </section>
          </div>
        )}
        {reasonEditor && (
          <div className="cv-status-modal-overlay" onClick={() => setReasonEditor(null)}>
            <div className="cv-status-modal" role="dialog" aria-label="VIN status reason" onClick={(e) => e.stopPropagation()}>
              <div className="cv-status-modal-header">
                <h3>{reasonEditor.status} — reason</h3>
                <button className="cv-status-modal-close" aria-label="Close" onClick={() => setReasonEditor(null)}>×</button>
              </div>
              <div className="cv-status-modal-body">
                <p>
                  Optional note on why{' '}
                  {reasonEditor.vins.length === 1 ? <strong>{reasonEditor.vins[0]}</strong> : `${reasonEditor.vins.length} VINs`}
                  {' '}{reasonEditor.vins.length === 1 ? 'has' : 'have'} this status.
                </p>
                <textarea
                  className="cv-status-reason-input"
                  autoFocus
                  value={reasonEditor.draft}
                  maxLength={VIN_STATUS_REASON_MAX}
                  rows={4}
                  placeholder="e.g. Client is retailing it on their lot"
                  onChange={(e) => setReasonEditor({ ...reasonEditor, draft: e.target.value })}
                />
                <div className="cv-status-reason-actions">
                  <span className="cv-status-reason-count">{reasonEditor.draft.length}/{VIN_STATUS_REASON_MAX}</span>
                  <button type="button" className="cv-vins-bulk-btn cv-vins-bulk-btn-secondary" onClick={() => setReasonEditor(null)}>
                    Cancel
                  </button>
                  <button type="button" className="cv-vins-bulk-btn" onClick={saveReason}>Save reason</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showBulkStatusModal && (
          <div className="cv-status-modal-overlay" onClick={() => setShowBulkStatusModal(false)}>
            <div className="cv-status-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cv-status-modal-header">
                <h3>Assign Status</h3>
                <button className="cv-status-modal-close" aria-label="Close" onClick={() => setShowBulkStatusModal(false)}>×</button>
              </div>
              <div className="cv-status-modal-body">
                <p>Select a status for <strong>{selectedVins.size}</strong> VIN{selectedVins.size > 1 ? 's' : ''}:</p>
                <label className="cv-status-reason-label">
                  Reason (optional, applies to every selected VIN)
                  <textarea
                    className="cv-status-reason-input"
                    value={bulkReason}
                    maxLength={VIN_STATUS_REASON_MAX}
                    rows={2}
                    placeholder="e.g. Client is retailing it on their lot"
                    onChange={(e) => setBulkReason(e.target.value)}
                  />
                </label>
                <div className="cv-status-options">
                  {USER_STATUS_OPTIONS.map((status) => (
                    <button
                      key={status || 'clear'}
                      type="button"
                      className={`cv-status-option ${statusBadgeClass(status)}`}
                      onClick={() => handleBulkStatusAssign(status)}
                    >
                      {status}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="cv-status-option status-badge status-unassigned"
                    onClick={() => handleBulkStatusAssign(null)}
                  >
                    Clear Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

// Helpers — describe how far a dealer is from a Core threshold.
// null = already Core (no gap); 0 = threshold met; >0 = remaining count.
function coreNeedLabel(n?: number | null): string {
  if (n == null) return 'Core ✓';
  if (n <= 0) return 'Met ✓';
  return `${Math.round(n).toLocaleString()} to go`;
}

// How far into its calendar quarter a quarter-to-date window has run. Returns
// null once the window covers the whole quarter (i.e. a completed one).
function quarterElapsed(
  start?: string | null,
  end?: string | null,
): { elapsed: number; total: number } | null {
  if (!start || !end) return null;
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  const qEnd = new Date(s);
  qEnd.setMonth(qEnd.getMonth() + 3);
  qEnd.setDate(qEnd.getDate() - 1);
  const day = 86400000;
  const total = Math.round((qEnd.getTime() - s.getTime()) / day) + 1;
  const elapsed = Math.round((e.getTime() - s.getTime()) / day) + 1;
  if (elapsed >= total) return null;
  return { elapsed, total };
}
